/**
 * SEO Component using React Helmet
 */
import React from 'react'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'

interface SEOProps {
  title: string
  description?: string
  lang?: string
  meta?: Array<{ name: string; content: string } | { property: string; content: string }>
}

const SEO: React.FC<SEOProps> = ({ description = '', lang = 'zh-TW', meta = [], title }) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
        }
      }
    }
  `)

  const metaDescription = description || site.siteMetadata.description

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        { rel: `icon`, href: `/favicon-32x32.png`, type: `image/png` },
        { rel: `manifest`, href: `/manifest.webmanifest`, crossOrigin: `anonymous` },
        { rel: `apple-touch-icon`, sizes: `48x48`, href: `/icons/icon-48x48.png` },
        { rel: `apple-touch-icon`, sizes: `72x72`, href: `/icons/icon-72x72.png` },
        { rel: `apple-touch-icon`, sizes: `96x96`, href: `/icons/icon-96x96.png` },
        { rel: `apple-touch-icon`, sizes: `144x144`, href: `/icons/icon-144x144.png` },
        { rel: `apple-touch-icon`, sizes: `192x192`, href: `/icons/icon-192x192.png` },
        { rel: `apple-touch-icon`, sizes: `256x256`, href: `/icons/icon-256x256.png` },
        { rel: `apple-touch-icon`, sizes: `384x384`, href: `/icons/icon-384x384.png` },
        { rel: `apple-touch-icon`, sizes: `512x512`, href: `/icons/icon-512x512.png` },
      ]}
      meta={[
        {
          name: `viewport`,
          content: `width=1200`,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        ...meta,
      ]}
    />
  )
}

export default SEO
